import React, { useState, useEffect } from "react";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Skeleton,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
  IconButton,
  Collapse,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import DescriptionIcon from "@mui/icons-material/Description";
import ErrorPage from "../Components/ErrorPage";
import StockAdd from "./StockAdd";
import StockEdit from "./StockEdit";
import useDelete from "../../api/useDelete";
import { useTranslation } from "react-i18next";
import { exportStockToPDF, exportStockToExcel, exportStockToCSV, printStockList } from "../../utils/exportStock";

import { formatJalaliDateTime } from "../utils/dateFormat";
const getAvailableQty = (stock) => {
  const rawQty = stock?.availableQty ?? stock?.quantity ?? 0;
  return Number(rawQty) || 0;
};

const getStockProduct = (stock) =>
  stock?.product_details || stock?.purchase_item_details?.product || null;

const getStockSupplier = (stock) =>
  stock?.supplier_details || stock?.purchase_item_details?.purchase?.supplier || null;

const getStockBuyPrice = (stock) =>
  stock?.buy_price || stock?.purchase_item_details?.buy_price || "-";

const getStockWholesalePrice = (stock) =>
  stock?.wholesale_price || stock?.purchase_item_details?.wholesale_price || "-";

const getStockRetailPrice = (stock) =>
  stock?.retail_price ||
  stock?.purchase_item_details?.retail_price ||
  stock?.purchase_item_details?.sale_price ||
  stock?.sale_price ||
  "-";

const StockRow = ({ stock, onEdit, onDelete, index }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const renderStockDetails = () => {
    return (
      <Table size="small" aria-label="stock details">
        <TableHead>
          <TableRow>
            <TableCell>{t("stock.product")}</TableCell>
            <TableCell>{t("stock.quantity")}</TableCell>
            <TableCell>{t("stock.buyPrice")}</TableCell>
            <TableCell>{t("stock.wholesalePrice")}</TableCell>
            <TableCell>{t("stock.retailPrice")}</TableCell>
            <TableCell>{t("stock.unit")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>
              {getStockProduct(stock)?.name || "--"}
            </TableCell>
            <TableCell>
              {getAvailableQty(stock) || "-"}
            </TableCell>
            <TableCell sx={{ color: "#e65100", fontWeight: 600 }}>
              {getStockBuyPrice(stock)}
            </TableCell>
            <TableCell sx={{ color: "#1b5e20", fontWeight: 600 }}>
              {getStockWholesalePrice(stock)}
            </TableCell>
            <TableCell sx={{ color: "#6a1b9a", fontWeight: 600 }}>
              {getStockRetailPrice(stock)}
            </TableCell>
            <TableCell>
              {stock?.unit_details?.title || "-"}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  };

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
          {index}
        </TableCell>
        <TableCell>
          {getStockProduct(stock)?.name || "--"}
        </TableCell>
        <TableCell>
          {getStockSupplier(stock)?.name || "--"}
        </TableCell>
        <TableCell sx={{ color: "#e65100", fontWeight: 600 }}>
          {getStockBuyPrice(stock)}
        </TableCell>
        <TableCell sx={{ color: "#1b5e20", fontWeight: 600 }}>
          {getStockWholesalePrice(stock)}
        </TableCell>
        <TableCell sx={{ color: "#6a1b9a", fontWeight: 600 }}>
          {getStockRetailPrice(stock)}
        </TableCell>
        <TableCell>{getAvailableQty(stock)}</TableCell>
        <TableCell>{stock?.unit_details?.title || "-"}</TableCell>
        <TableCell>
          {stock?.currency_details?.title || "-"}
        </TableCell>
        <TableCell>{stock?.shop_details?.name || "-"}</TableCell>
        <TableCell>
          <Button
            variant="outlined"
            size="small"
            sx={{ mr: 1 }}
            onClick={() => onEdit(stock)}
          >
            {t("stock.edit")}
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => onDelete(stock.id)}
          >
            {t("stock.delete")}
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t("stock.purchaseItemsDetail")}
              </Typography>
              {renderStockDetails()}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const StockIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  // Pagination states
  const [searchTerm, setSearchTerm] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [page, setPage] = useState(1); // one-based indexing
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal states
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  // Export menu state
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportMenuOpen = Boolean(exportAnchorEl);

  // Fetch products that are in stock for filter dropdown
  const { data: stockProducts } = useFetchObjects(
    ["stockProducts"],
    "shop/stocks/?page_size=1000",
    token
  );

  // Extract unique products from stock data
  const uniqueProducts = React.useMemo(() => {
    if (!stockProducts?.results) return [];
    const productMap = new Map();
    stockProducts.results.forEach(stock => {
      const product = getStockProduct(stock);
      if (product) {
        productMap.set(product.id, product);
      }
    });
    return Array.from(productMap.values());
  }, [stockProducts]);

  // Build API URL with filters
  const buildApiUrl = () => {
    const params = new URLSearchParams();
    
    if (searchTerm) {
      params.append('search', searchTerm);
    }
    
    if (productFilter) {
      params.append('purchase_item__product', productFilter);
    }
    
    if (rowsPerPage === "all") {
      params.append('page_size', 'all');
    } else {
      params.append('page', page.toString());
      params.append('page_size', rowsPerPage.toString());
    }
    
    return `shop/stocks/?${params.toString()}`;
  };

  // Fetch stock records with search and pagination parameters.
  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["stock", searchTerm, productFilter, page, rowsPerPage],
    buildApiUrl(),
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete("shop/stocks", token);

  // Refetch whenever search, product, page or rowsPerPage changes.
  useEffect(() => {
    refetch();
  }, [page, rowsPerPage, searchTerm, productFilter, refetch]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleProductChange = (e) => {
    setProductFilter(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    setRowsPerPage(value === 'all' ? 'all' : parseInt(value));
    setPage(1);
  };

  // Determine stocks list and total count from response
  const stocks = data?.results || data || [];
  const totalCount = data?.count || (Array.isArray(data) ? data.length : 0);
  const totalPages = rowsPerPage === "all" ? 1 : Math.ceil(totalCount / rowsPerPage);

  // Export handlers
  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const handleExportPDF = () => {
    exportStockToPDF(stocks, searchTerm, productFilter);
    handleExportClose();
  };

  const handleExportExcel = () => {
    exportStockToExcel(stocks);
    handleExportClose();
  };

  const handleExportCSV = () => {
    exportStockToCSV(stocks);
    handleExportClose();
  };

  const handlePrint = () => {
    printStockList(stocks);
    handleExportClose();
  };

  if (isError) {
    return <ErrorPage message={t("stock.failedToLoadStocks")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">{t("stock.stockList")}</Typography>
        
        {/* Export Actions */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenAdd(true)}
          >
            {t("stock.addStock")}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportClick}
            sx={{
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              color: "white",
              "&:hover": {
                background: "linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)",
              },
            }}
          >
            {t("stock.export")}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              borderColor: "#667eea",
              color: "#667eea",
              "&:hover": {
                borderColor: "#5a6fd8",
                backgroundColor: "rgba(102, 126, 234, 0.04)",
              },
            }}
          >
            {t("stock.print")}
          </Button>
        </Box>
      </Box>

      {/* Export Menu */}
      <Menu
        anchorEl={exportAnchorEl}
        open={exportMenuOpen}
        onClose={handleExportClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            borderRadius: 2,
          },
        }}
      >
        <MenuItem onClick={handleExportPDF}>
          <ListItemIcon>
            <PictureAsPdfIcon sx={{ color: "#d32f2f" }} />
          </ListItemIcon>
          <ListItemText primary={t("stock.exportPDF")} />
        </MenuItem>
        
        <MenuItem onClick={handleExportExcel}>
          <ListItemIcon>
            <TableViewIcon sx={{ color: "#2e7d32" }} />
          </ListItemIcon>
          <ListItemText primary={t("stock.exportExcel")} />
        </MenuItem>
        
        <MenuItem onClick={handleExportCSV}>
          <ListItemIcon>
            <DescriptionIcon sx={{ color: "#ed6c02" }} />
          </ListItemIcon>
          <ListItemText primary={t("stock.exportCSV")} />
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handlePrint}>
          <ListItemIcon>
            <PrintIcon sx={{ color: "#1976d2" }} />
          </ListItemIcon>
          <ListItemText primary={t("stock.print")} />
        </MenuItem>
      </Menu>
      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("stock.dataLoadedOn")} {formatJalaliDateTime()}
      </Typography>
      {/* Search and Filter Fields */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <TextField
            variant="outlined"
            fullWidth
            placeholder={t("stock.searchStocks")}
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <Select
              value={productFilter}
              onChange={handleProductChange}
              displayEmpty
              variant="outlined"
            >
              <MenuItem value="">
                {t("stock.allProducts") || "All Products"}
              </MenuItem>
              {uniqueProducts.map((product) => (
                <MenuItem key={product.id} value={product.id}>
                  {product.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      {/* Stock Table */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table sx={{ minWidth: 750 }} aria-label="stock table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("stock.id")}</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("stock.product")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("stock.supplier")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("stock.buyPrice")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("stock.wholesalePrice")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("stock.retailPrice")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("stock.availableQty")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>{t("stock.unit")}</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("stock.currency")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>{t("stock.shop")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("stock.action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {[
                      "ID",
                      "Product",
                      "Supplier",
                      "Buy Price",
                      "Wholesale Price",
                      "Retail Price",
                      "Available Qty",
                      "Unit",
                      "Currency",
                      "Branch",
                      "Action",
                    ].map((_, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : stocks.map((stock, index) => (
                  <StockRow
                    key={stock.id}
                    stock={stock}
                    index={
                      rowsPerPage === "all"
                        ? index + 1
                        : (page - 1) * rowsPerPage + index + 1
                    }
                    onEdit={(data) => {
                      setEditData(data);
                      setOpenEdit(true);
                    }}
                    onDelete={(id) => handleDelete(id)}
                  />
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination */}
      {rowsPerPage !== 'all' && (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          padding="10px"
        >
          <Box display="flex" alignItems="center">
            <Typography>{t("stock.rowsPerPage")}</Typography>
            <FormControl
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    border: "none",
                  },
                },
              }}
            >
              <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={"all"}>All</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            color="primary"
          />
        </Grid>
      )}
      
      {rowsPerPage === 'all' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Showing all {totalCount} stock items
          </Typography>
        </Box>
      )}
      {openAdd && (
        <StockAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          onSaved={() => refetch()}
          token={token}
        />
      )}
      {openEdit && (
        <StockEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}
      <ConfirmDialog />
    </Box>
  );
};

export default StockIndex;
